import { BulkApprovalBar } from "../BulkApprovalBar";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { customGetAPI, customAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import {
  scrollTop,
  getStatusFlow,
  StatusBadge,
  displayFlexItem,
} from "services/constants";
import { Apply } from "./Apply";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { showToast } from "services/utils/Status";
import { TeamZoneAdvanceSalary } from "../Headers";
import { AdvanceSalaryDetailPanel } from "./DetailPanel";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import {
  AdvanceSalaryStatusCards,
  canonicalAdvanceSalaryStatus,
  advanceSalaryStatusVisual,
} from "./AdvanceSalaryStatusCards";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  bulkSelectionCheckboxSx,
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import moment from "moment";
import { resolveRangeLabel } from "../dateRangeUtils";

// Same "Default View" sentinel pattern as the other split screens.
const ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID =
  "__default_advance_salary_list_view__";

const ADVANCE_TYPE_STYLES = {
  Salary: { label: "Salary Advance", color: "#2878FF", bg: "#EAF1FF" },
  Business: { label: "Business Advance", color: "#00AB66", bg: "#E7F7EF" },
};

// getStatusFlow only defines Pending/Approved/Completed/Rejected/Cancelled,
// but an advance's post-approval lifecycle has more statuses. Closed and
// Pre-Closed mean the advance is finished (Completed); Active/Partially-Paid
// are still being repaid (Approved). Without this the expanded row looked up
// a flow key that doesn't exist and rendered no status at all.
const advanceStatusFlowKey = (status) => {
  const key = String(status || "").trim().toLowerCase();
  if (key === "closed" || key === "pre-closed") return "Completed";
  if (key === "approved" || key === "active" || key === "partially-paid") {
    return "Approved";
  }
  return status;
};

const AdvanceTypeBadge = ({ type }) => {
  const style = ADVANCE_TYPE_STYLES[type] || {
    label: type || "N/A",
    color: designColors.slate700,
    bg: designColors.slate100,
  };

  return (
    <Mui.Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.375,
        borderRadius: 1.5,
        bgcolor: style.bg,
        color: style.color,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {style.label}
    </Mui.Box>
  );
};

const TeamAdvanceSalaryContent = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const {
    domain,
    userDetails,
    topbarCompanyId,
    topbarCompanyName,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const containerRef = useRef(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [advanceSalaryData, setAdvanceSalaryData] = useState([]);
  const [advanceSalaryFilterData, setAdvanceSalaryFilterData] = useState([]);
  const [viewAdvanceSalaryDetails, setViewAdvanceSalaryDetails] =
    useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [employeeInfoAnchor, setEmployeeInfoAnchor] = useState(null);
  const [employeeInfoData, setEmployeeInfoData] = useState(null);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [bulkRejectReasonError, setBulkRejectReasonError] = useState("");

  const openEmployeeInfo = (event, value) => {
    event.stopPropagation();
    setEmployeeInfoAnchor(event.currentTarget);
    setEmployeeInfoData(value);
  };

  const closeEmployeeInfo = () => {
    setEmployeeInfoAnchor(null);
    setEmployeeInfoData(null);
  };
  // Initialized from (and kept in sync with) the Approval Center landing
  // page's own dateRangeLabel prop — previously hardcoded to "Last 7 Days"
  // regardless of the actual startDate/endDate this screen was handed, on
  // a fresh ?view= deep link or Team Workspace Home's "date" param. A
  // manual re-pick on this screen's own picker still overrides it locally.
  const [dateRangeLabel, setDateRangeLabel] = useState(
    dateRangeLabelProp || "Last 7 Days",
  );

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);
  const {
    expanded: advanceSalaryPanelExpanded,
    toggleExpanded: toggleAdvanceSalaryPanelExpanded,
  } = useExpandablePanel(viewAdvanceSalaryDetails);
  const {
    expanded: createAdvanceSalaryPanelExpanded,
    toggleExpanded: toggleCreateAdvanceSalaryPanelExpanded,
  } = useExpandablePanel(isCreatePanelOpen);

  useEffect(() => {
    const fetchAdvanceSalaryDetails = async () => {
      const res = await customGetAPI(
        `${domain}advance/request/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}&start_date=${startDate}&end_date=${endDate}`,
      );
      const rows = res?.responseDetails?.data ?? [];
      const mappedRows = rows.map((row) => ({
        ...row,
        applied_on: row.applied_at ?? row.applied_on,
        // Same approved_by/rejected_by/approver_name fallback chain
        // DetailPanel.js already uses for this screen's status timeline.
        approver_name:
          row.approved_by || row.rejected_by || row.approver_name || "-",
      }));
      setAdvanceSalaryData(mappedRows);
      // Also seed the filtered view directly — CustomFilter recomputes it
      // in its own effect a render later, so without this the table
      // flashes "No Matching Results" for a frame right as loading ends.
      setAdvanceSalaryFilterData(mappedRows);
      setLoading(false);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) {
      setLoading(true);
      fetchAdvanceSalaryDetails();
    }
  }, [
    domain,
    endDate,
    reCallApi,
    setReCallApi,
    startDate,
    topbarCompanyId,
    userDetails?.id,
  ]);

  // Access control is tied to the real "Advance Salary" backend submodule
  // (unlike Permission/OnDuty/CompOff/Break, this screen has always had
  // its own submodule — no WorkAdjustment lookup indirection needed).
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Approvals",
    "Advance Salary",
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setAdvanceSalaryFilterData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  // Tallies for the Pending/Approved/Rejected cards, computed from the full
  // date-range fetch (advanceSalaryData) rather than advanceSalaryFilterData
  // — so the counts stay stable while the user searches/sorts via CustomFilter.
  const advanceSalaryStatusCounts = useMemo(
    () =>
      (advanceSalaryData || []).reduce(
        (acc, request) => {
          const status = canonicalAdvanceSalaryStatus(request?.status);
          acc["Total Requests"] += 1;

          if (status === "Cancelled") {
            acc.Cancelled += 1;
          } else if (request?.disbursement_date) {
            acc.Paid += 1;
          } else if (status === "Pending") {
            acc.Pending += 1;
          } else if (status === "Approved") {
            acc.Approved += 1;
          } else if (status === "Rejected") {
            acc.Rejected += 1;
          }

          return acc;
        },
        {
          "Total Requests": 0,
          Pending: 0,
          Approved: 0,
          Paid: 0,
          Rejected: 0,
          Cancelled: 0,
        },
      ),
    [advanceSalaryData],
  );

  const visibleAdvanceSalaryData = useMemo(() => {
    if (!statusCardFilter) return advanceSalaryFilterData;
    if (statusCardFilter === "Paid") {
      return advanceSalaryFilterData.filter(
        (request) => request?.disbursement_date,
      );
    }
    return advanceSalaryFilterData.filter(
      (request) =>
        canonicalAdvanceSalaryStatus(request?.status) === statusCardFilter,
    );
  }, [advanceSalaryFilterData, statusCardFilter]);

  const handleStatusCardClick = (key) => {
    clearSelection();
    setShowBulkRejectDialog(false);
    setPage(0);
    setExpandedIndex(null);
    setExpandedData(null);
    setViewAdvanceSalaryDetails(false);
    setSelectedData(null);
    if (key === "Total Requests") {
      setStatusCardFilter("");
    } else {
      setStatusCardFilter(key);
    }
  };

  // Bulk select/approve/reject — same multi-select action bar CompOff/
  // Expense's lists have. There is no bulk-ids endpoint for Advance Salary
  // (unlike Expense's teamworkspace-expense-approve/) — approve/reject are
  // per-request endpoints (advance/request/{id}/approve|reject/) — so bulk
  // actions fire one call per selected row via Promise.all, same as
  // Attendance Correction's per-(user_id,date) PUT does.
  const isSelected = (row) => selectedRowKeys.has(row.id);

  const pendingRows = useMemo(
    () =>
      visibleAdvanceSalaryData.filter(
        (row) => canonicalAdvanceSalaryStatus(row.status) === "Pending",
      ),
    [visibleAdvanceSalaryData],
  );

  const toggleRow = (row) => {
    if (canonicalAdvanceSalaryStatus(row.status) !== "Pending") return;
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
      return next;
    });
  };

  const toggleAll = () => {
    const allPendingSelected =
      pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
    setSelectedRowKeys(
      allPendingSelected ? new Set() : new Set(pendingRows.map((r) => r.id)),
    );
  };

  const isAllSelected =
    pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
  const isIndeterminate =
    selectedRowKeys.size > 0 && selectedRowKeys.size < pendingRows.length;

  const clearSelection = () => {
    setSelectedRowKeys(new Set());
    setBulkRejectReason("");
    setBulkRejectReasonError("");
  };

  const selectedRows = visibleAdvanceSalaryData.filter((r) => isSelected(r));

  // Same "quick approve" terms DetailPanel.js's handleQuickApprove sends —
  // approve using whatever repayment terms the employee originally requested.
  const buildApprovePayload = (row) => {
    const isBusinessAdvance = row.advance_type === "Business";
    if (isBusinessAdvance) {
      return {
        approver_id: userDetails?.id,
        approved_amount: row.amount,
        approver_comments: "Bulk approved from list view.",
      };
    }
    const repaymentDate = row.repayment_date || row.repayment_start_date;
    return {
      approver_id: userDetails?.id,
      approved_amount: row.amount,
      repayment_mode: row.repayment_mode,
      approved_installments:
        row.no_of_installments || row.number_of_installments || 1,
      approved_repayment_date: repaymentDate
        ? moment(repaymentDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : "",
      approver_comments: "Bulk approved from list view.",
      approved_repayment_frequency: row.repayment_frequency || "Monthly",
    };
  };

  const putAdvanceSalaryApprove = (row) =>
    customAPI(
      `${domain}advance/request/${row.id}/approve/?company_id=${topbarCompanyId}`,
      "PUT",
      buildApprovePayload(row),
    );

  const putAdvanceSalaryReject = (row, reason) =>
    customAPI(`${domain}advance/request/${row.id}/reject/?company_id=${topbarCompanyId}`, "PUT", {
      approver_id: userDetails?.id,
      approver_comments: "",
      rejection_reason: reason,
    });

  const validateBulkRejectReason = () => {
    const reason = bulkRejectReason.trim();
    if (!reason) return "Reject reason is required";
    if (reason.length < 3) return "Minimum 3 characters required";
    return "";
  };

  const handleBulkApprove = async () => {
    if (!selectedRows.length) return;
    setBulkLoading(true);
    const responses = await Promise.all(
      selectedRows.map((row) => putAdvanceSalaryApprove(row)),
    );
    if (responses.every((res) => res?.ok)) {
      showToast("success", "Records approved.");
      clearSelection();
      setReCallApi(true);
    } else {
      showToast("error", "Failed to approve one or more requests.");
    }
    setBulkLoading(false);
  };

  const handleBulkReject = async () => {
    if (!selectedRows.length) return;
    const reasonError = validateBulkRejectReason();
    if (reasonError) {
      setBulkRejectReasonError(reasonError);
      return;
    }
    setBulkLoading(true);
    const responses = await Promise.all(
      selectedRows.map((row) => putAdvanceSalaryReject(row, bulkRejectReason)),
    );
    if (responses.every((res) => res?.ok)) {
      showToast("success", "Records rejected.");
      clearSelection();
      setShowBulkRejectDialog(false);
      setReCallApi(true);
    } else {
      showToast("error", "Failed to reject one or more requests.");
    }
    setBulkLoading(false);
  };

  const handleAdvanceSalaryDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const advanceSalaryColumnHeaders = useMemo(
    () => TeamZoneAdvanceSalary.filter((header) => header.key !== "expand"),
    [],
  );

  // Default View / saved-view selector — same shape as the other split
  // screens.
  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_advance_salary_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId =
    activeViewId || ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(
    () => advanceSalaryColumnHeaders,
    [advanceSalaryColumnHeaders],
  );

  // storedDefaultView reads back whatever handleEditDefaultView below last
  // persisted (it always saves with isCustomDefault: true) -- this was
  // missing here, unlike every sibling Approve screen (Leave/OverTime/etc.),
  // so editing this screen's Default View persisted correctly but the
  // customization was never read back: defaultView below always
  // regenerated the plain full-column list, ignoring what was saved.
  // Editing it via Update looked like it silently did nothing.
  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(() => {
    if (storedDefaultView?.isCustomDefault) {
      // Whatever headerConfig the user saved is authoritative — same as any
      // other saved view (see useSaveView.js's identical comment for why
      // this doesn't back-fill headers missing from headerConfig).
      return storedDefaultView;
    }

    return {
      id: ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID,
      name: "Default View",
      headerConfig: baseDefaultHeaders.map((header) => ({
        key: header.key,
        name: header.name,
      })),
      headerKeys: baseDefaultHeaders.map((header) => header.key),
      isDefaultView: true,
    };
  }, [baseDefaultHeaders, storedDefaultView]);

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      advanceSalaryColumnHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = advanceSalaryColumnHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, advanceSalaryColumnHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={advanceSalaryColumnHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={check_access}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === ADVANCE_SALARY_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      check_access,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      advanceSalaryColumnHeaders,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, topbarCompanyName],
  );

  const newAdvanceSalaryButton = (
    <NewRequestButton
      hasAccess={check_access}
      deniedMessage="Access denied: You are not authorized to create a salary advance request"
      onClick={() => setIsCreatePanelOpen(true)}
    />
  );

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box sx={{ flexGrow: 1, width: "100%", overflow: "visible" }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>
              Advance Salary
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review and process salary advance requests awaiting approval
            </Mui.Typography>
          </Box>

          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleAdvanceSalaryDateRange}
              includeCurrentDate
              showToday
            />
            {newAdvanceSalaryButton}
          </Mui.Stack>
        </Box>

        <AdvanceSalaryStatusCards
          counts={advanceSalaryStatusCounts}
          activeFilter={statusCardFilter}
          onCardClick={handleStatusCardClick}
          totalRequestsDetail={dateRangeLabel}
        />

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title={"Advance Salary Requests"}
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={advanceSalaryData}
            filteredData={visibleAdvanceSalaryData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            labeledToolbar={true}
          />
        </Box>
        <BulkApprovalBar
          selectedCount={statusCardFilter === "Pending" ? selectedRowKeys.size : 0}
          loading={bulkLoading}
          onReject={() => setShowBulkRejectDialog(true)}
          onApprove={handleBulkApprove}
        />
        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
            <ApproveTable
              stickyHeader
              sx={{
                minWidth: 920,
                width: "100%",
                tableLayout: "fixed",
              }}
            >
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {statusCardFilter === "Pending" && (
                    <Mui.TableCell
                      padding="checkbox"
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      {statusCardFilter === "Pending" && pendingRows.length > 0 && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={toggleAll}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                  <Mui.TableCell
                    sx={{
                      ...approveTableHeadCell,
                      top: APP_BAR_OFFSET + customFilterHeight,
                      width: 40,
                    }}
                  />
                  {visibleHeaders?.map((value) => (
                    <Mui.TableCell
                      key={value.key}
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      <TruncatedTableValue value={value?.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleAdvanceSalaryData?.length === 0 ? (
                  <TableEmptyState
                    dataLength={advanceSalaryData}
                    colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                  />
                ) : (
                  visibleAdvanceSalaryData
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                    ?.map((value, index) => (
                      <React.Fragment key={value?.id}>
                        <Mui.TableRow
                          onClick={() => {
                            setSelectedData(value);
                            setViewAdvanceSalaryDetails(true);
                          }}
                          sx={{
                            cursor: "pointer",
                            ...designShell.tableRowHover,
                            ...(viewAdvanceSalaryDetails &&
                            value?.id === selectedData?.id
                              ? designShell.neutralSelectedRow
                              : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          {statusCardFilter === "Pending" && (
                            <Mui.TableCell
                              padding="checkbox"
                              sx={approveTableBodyCell}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {statusCardFilter === "Pending" && canonicalAdvanceSalaryStatus(value?.status) === "Pending" && (
                                <Mui.Checkbox
                                  size="small"
                                  sx={bulkSelectionCheckboxSx}
                                  checked={isSelected(value)}
                                  onChange={() => toggleRow(value)}
                                />
                              )}
                            </Mui.TableCell>
                          )}
                          <Mui.TableCell
                            sx={approveTableBodyCell}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedIndex((prev) =>
                                prev === index ? null : index,
                              );
                              setExpandedData(value);
                            }}
                          >
                            <ArrowRightIcon
                              sx={{
                                color: designColors.slate500,
                                cursor: "pointer",
                                transition: "transform 0.2s ease",
                                transform:
                                  expandedIndex === index
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                              }}
                            />
                          </Mui.TableCell>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell
                              key={head.key} data-label={head.name}
                              sx={approveTableBodyCell}
                            >
                              {head.key === "user_name" ? (
                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  onClick={(event) => openEmployeeInfo(event, value)}
                                  sx={{ cursor: "pointer", width: "fit-content" }}
                                >
                                  <Mui.Avatar
                                    sx={{ width: 28, height: 28 }}
                                    alt={value?.user_name?.charAt(0)?.toUpperCase()}
                                    src={value?.image_url}
                                  />
                                  <Mui.Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 500,
                                      color: designColors.slate900,
                                      "&:hover": { textDecoration: "underline" },
                                    }}
                                  >
                                    {value?.user_name || "N/A"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              ) : head.key === "request_id" ? (
                                <Mui.Typography
                                  sx={{
                                    fontWeight:
                                      viewAdvanceSalaryDetails &&
                                      value?.id === selectedData?.id
                                        ? 700
                                        : 600,
                                    color:
                                      viewAdvanceSalaryDetails &&
                                      value?.id === selectedData?.id
                                        ? designColors.slate900
                                        : designColors.slate800,
                                    fontSize: 14,
                                  }}
                                >
                                  {value?.request_id ||
                                    `ADV-${value?.id || "N/A"}`}
                                </Mui.Typography>
                              ) : head.key === "advance_type" ? (
                                <AdvanceTypeBadge type={value?.advance_type} />
                              ) : head.key === "status" ? (
                                <Mui.Box sx={displayFlexItem}>
                                  <StatusBadge
                                    color={
                                      value?.color_code ||
                                      advanceSalaryStatusVisual(value?.status).accent
                                    }
                                    label={value?.status || "N/A"}
                                  />
                                </Mui.Box>
                              ) : (
                                (<TruncatedTableValue value={value[head.key] ?? "N/A"} />)
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>

                        {expandedIndex === index && (
                          <Mui.TableRow>
                            <Mui.TableCell
                              colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                              sx={{
                                p: 2,
                                borderBottom: `1px solid ${designColors.slate200}`,
                              }}
                            >
                              {getStatusFlow(expandedData)[
                                advanceStatusFlowKey(expandedData?.status)
                              ]?.map((item, i, arr) => (
                                <StatusRow
                                  key={i}
                                  status={item.status}
                                  user={item.user}
                                  date={item.date}
                                  showConnector={i !== arr.length - 1}
                                />
                              ))}
                              <ApprovalAuditTrail record={expandedData} compact />
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleAdvanceSalaryData?.length || 0,
              itemLabel: "advance salary requests",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>
      <Apply
        open={isCreatePanelOpen}
        onClose={() => {
          handleCreateClosePanel();
        }}
        setReCallApi={setReCallApi}
        expanded={createAdvanceSalaryPanelExpanded}
        onToggleExpand={toggleCreateAdvanceSalaryPanelExpanded}
      />
      <AdvanceSalaryDetailPanel
        open={viewAdvanceSalaryDetails}
        onClose={() => setViewAdvanceSalaryDetails(false)}
        advanceSalary={selectedData}
        checkAccess={check_access}
        setReCallApi={setReCallApi}
        expanded={advanceSalaryPanelExpanded}
        onToggleExpand={toggleAdvanceSalaryPanelExpanded}
      />
      <Mui.Popover
        open={Boolean(employeeInfoAnchor)}
        anchorEl={employeeInfoAnchor}
        onClose={closeEmployeeInfo}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { borderRadius: "14px", boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)" } } }}
      >
        <Mui.Box sx={{ p: 1.5, width: 320, maxWidth: "100%" }}>
          {employeeInfoData ? (
            <RequestDetailHeader
              userDetails={{
                image_url: employeeInfoData?.image_url,
                employee_id: employeeInfoData?.employee_id,
                user_name: employeeInfoData?.user_name,
                department_name:
                  employeeInfoData?.department_name || employeeInfoData?.department,
                designation: employeeInfoData?.designation,
              }}
              status={employeeInfoData?.status}
              colorCode={
                employeeInfoData?.color_code ||
                advanceSalaryStatusVisual(employeeInfoData?.status).accent
              }
            />
          ) : null}
        </Mui.Box>
      </Mui.Popover>
      <GlobalConfirmDialog
        open={showBulkRejectDialog}
        onCancel={() => {
          setShowBulkRejectDialog(false);
          setBulkRejectReason("");
          setBulkRejectReasonError("");
        }}
        onConfirm={handleBulkReject}
        loading={bulkLoading}
        title="Reject Selected Requests"
        confirmLabel="Reject"
        cancelLabel="Cancel"
        danger
        maxWidth="sm"
        reasonValue={bulkRejectReason}
        reasonError={bulkRejectReasonError}
        onReasonChange={(value) => {
          setBulkRejectReason(value);
          if (bulkRejectReasonError) setBulkRejectReasonError("");
        }}
        reasonPlaceholder="Enter a reason for rejection"
        reasonMinRows={2}
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Reason - applied to all {selectedRowKeys.size} selected request(s).
        </Mui.Typography>
      </GlobalConfirmDialog>
    </React.Fragment>
  );
};

export const TeamAdvanceSalary = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel,
  handleStartDate,
  subModuleId,
}) => (
  <TeamAdvanceSalaryContent
    setReCallApi={setReCallApi}
    reCallApi={reCallApi}
    startDate={startDate}
    endDate={endDate}
    dateRangeLabel={dateRangeLabel}
    handleStartDate={handleStartDate}
    subModuleId={subModuleId}
  />
);
